import axios from 'axios';
import { findDestinationCountry, getMapfreDestinationZone } from '@/constants/travelCountries';

export class TravelPolicyService {
  static setTravelContext(store, formData, authenticatedUser = null) {
    // console.log('Updating travel context with form data');
    // console.log('Form data:', formData);
    // console.log('Authenticated user:', authenticatedUser);
    // console.log('Is buying for someone else:', formData.isBuyingForSomeoneElse);

    // When buying for someone else, always treat as non-resident regardless of buyer's status
    const isResident = authenticatedUser && authenticatedUser['custom:id_number'] && !formData.isBuyingForSomeoneElse;
    const passportDocument = formData.passport_number || formData.passport_no || 'UNKNOWN';
    const idNumber = isResident
      ? authenticatedUser['custom:id_number']
      : (formData.id_number || formData.id_no);

    const travelContext = {
      isResident,
      insuredFirstName: formData.first_name || 'Unknown',
      insuredSurname: formData.last_name || 'Unknown',
      // insuredFirstName: 'TEST',
      // insuredSurname: 'TEST',
      insuredPassport: passportDocument,
      policyholderFirstName: formData.first_name || 'Unknown',
      policyholderSurname: formData.last_name || 'Unknown',
      policyholderPassport: passportDocument,
      selectedCountry: formData.destination_country || null,
      email: formData.email,
      phone_number: formData.phone_number || formData.mobile_main,
      mobile_alternative: formData.mobile_alternative,
      gender: formData.gender,
      date_of_birth: formData.date_of_birth,
      id_no: idNumber,
      kra_pin: formData.kra_pin,
      passport_no: formData.passport_no,
      address: formData.address,
      next_of_kin: formData.next_of_kin,
      beneficiaries: formData.beneficiaries,
      politically_exposed: formData.politically_exposed,
      travel_start_date: formData.travel_start_date,
      travel_end_date: formData.travel_end_date,
      travel_plan: formData.travel_plan,
      declaration: formData.declaration,
      data_privacy: formData.data_privacy,
      residency: formData.residency,
    };

    // console.log('Travel context set for', isResident ? 'resident' : 'non-resident', ':', travelContext);
    store.commit('personalDataTravel/setTravelContext', travelContext);
    // console.log('Travel context updated successfully');

    return travelContext;
  }

  static async issueTravelPolicy(paymentContext, store = null) {
    try {
      // console.log('Issuing travel policy after successful payment...');

      // Fetch the full travel quote/policy data
      const policyResponse = await axios.get(`/customer/digital-quotation/pacis-travel/quotes/${paymentContext.productId}`);
      const travelPolicy = policyResponse.data;

      // Get travel context from store if available
      let travelContext = null;
      if (store) {
        try {
          travelContext = store.getters['personalDataTravel/getTravelContext'];
          // console.log('Retrieved travel context from store:', travelContext);
        } catch (error) {
          // console.warn('Failed to retrieve travel context from store:', error);
          travelContext = null;
        }
      }
      // console.log('Travel Context for Mapping:', travelContext);
      // console.log('Travel Context selectedCountry:', travelContext?.selectedCountry);
      // console.log('Travel Policy destination fields:', {
      //   destination: travelPolicy.destination,
      //   region: travelPolicy.region,
      //   selectedCountry: travelPolicy.selectedCountry,
      // });
      // console.log('Travel Context date fields:', {
      //   insuredDateOfBirth: travelContext?.insuredDateOfBirth,
      //   policyholderDateOfBirth: travelContext?.policyholderDateOfBirth,
      //   date_of_birth: travelContext?.date_of_birth,
      // });

      // Map travel policy data to Mapfre format
      const mapfreData = TravelPolicyService.mapTravelPolicyToMapfre(travelPolicy, travelContext);
      // console.log('Mapfre API payload:', JSON.stringify(mapfreData, null, 2));

      // Issue policy via Mapfre API
      const issuanceResponse = await axios.post('/customer/gis/mapfre/policy/issue', mapfreData, {
        headers: {
          // Authorization: 'Bearer {{authToken}}',
          'Content-Type': 'application/json',
        },
      });

      if (issuanceResponse.data.success && issuanceResponse.data.data.success) {
        const policyData = issuanceResponse.data.data;

        // Send policy email instead of showing download page
        // await TravelPolicyService.sendPolicyEmail(travelPolicy, policyData);

        // Update the policy status in backend
        await TravelPolicyService.updateTravelPolicyStatus(paymentContext.productId, policyData);

        return {
          success: true,
          policyNumber: policyData.policyNumber,
          message: `Policy ${policyData.policyNumber} issued successfully! Policy details have been sent to your email.`,
        };
      }
      throw new Error(issuanceResponse.data.message || 'Policy issuance failed');
    } catch (error) {
      // console.error('Failed to issue travel policy:', error);
      throw new Error('Policy issuance failed. Please contact support for assistance.');
    }
  }

  static mapTravelPolicyToMapfre(travelPolicy, travelContext) {
    // Determine product code based on destination zone, plan type and coverage level
    const prodCode = TravelPolicyService.getProductCode(travelPolicy, travelContext);

    /* eslint-disable sonarjs/no-duplicate-string */
    // Map insured data - handle multiple travel members for family policies
    let insuredData = [];
    if (travelContext?.travelMembers && Array.isArray(travelContext.travelMembers)) {
      insuredData = travelContext.travelMembers.map((member) => ({
        insuredFirstName: member.insuredFirstName || member.first_name || 'Unknown',
        insuredSurname: member.insuredSurname || member.last_name || 'Unknown',
        // insuredFirstName: 'TEST',
        // insuredSurname: 'TEST',
        insuredDateOfBirth: member.insuredDateOfBirth || member.date_of_birth || 'Unknown',
        insuredPassport: member.insuredPassport || member.passport_no || 'UNKNOWN',
      }));
    } else {
      // Fallback to single insured (policyholder)
      insuredData = [{
        insuredFirstName: travelContext?.insuredFirstName || travelPolicy.first_name || 'Unknown',
        insuredSurname: travelContext?.insuredSurname || travelPolicy.last_name || 'Unknown',
        // insuredFirstName: 'TEST',
        // insuredSurname: 'TEST',
        insuredDateOfBirth: travelContext?.insuredDateOfBirth || travelContext?.date_of_birth || travelPolicy.date_of_birth || 'Unknown',
        insuredPassport: travelContext?.insuredPassport || travelPolicy.passport_no || 'UNKNOWN',
      }];
      // console.log('Insured date mapping - using:', {
      //   'travelContext?.insuredDateOfBirth': travelContext?.insuredDateOfBirth,
      //   'travelContext?.date_of_birth': travelContext?.date_of_birth,
      //   'travelPolicy.date_of_birth': travelPolicy.date_of_birth,
      //   'final value': travelContext?.insuredDateOfBirth || travelContext?.date_of_birth || travelPolicy.date_of_birth || 'Unknown',
      // });
    }

    const basePayload = {
      prodCode,
      policyNumber: 'AUTO',
      email: travelPolicy.email_address || travelPolicy.email,
      startDate: travelPolicy.travel_start_date || new Date().toISOString().split('T')[0],
      endDate: travelPolicy.travel_end_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      agnShtDesc: this.selectedAgent || 'DIRECT',
      originCountry: 'KE',
      comment: `Travel insurance via customer portal - Quote ID: ${travelPolicy.id}`,
      zone: TravelPolicyService.getMapfreZone(travelPolicy.region, travelPolicy.selectedCountry),
      destinationCountry: travelContext?.selectedCountry
        || travelPolicy.selectedCountry
        || TravelPolicyService.getCountryCode(travelPolicy.destination)
        || TravelPolicyService.getDestinationCountry(travelPolicy.region)
        || null,
      insuredData,
      // policyholderFirstName: travelContext?.policyholderFirstName || travelPolicy.first_name || 'Unknown',
      // policyholderSurname: travelContext?.policyholderSurname || travelPolicy.last_name || 'Unknown',
      policyholderFirstName: 'TEST',
      policyholderSurname: 'TEST',
      policyholderPassport: travelContext?.policyholderPassport || travelPolicy.passport_no || 'UNKNOWN',
      policyholderDateOfBirth: travelContext?.policyholderDateOfBirth || travelContext?.date_of_birth || travelPolicy.date_of_birth || 'Unknown',
    };

    // console.log('Policyholder date mapping - using:', {
    //   'travelContext?.policyholderDateOfBirth': travelContext?.policyholderDateOfBirth,
    //   'travelContext?.date_of_birth': travelContext?.date_of_birth,
    //   'travelPolicy.date_of_birth': travelPolicy.date_of_birth,
    //   'final value': travelContext?.policyholderDateOfBirth || travelContext?.date_of_birth || travelPolicy.date_of_birth || 'Unknown',
    // });

    // Add premium if available
    if (travelPolicy.premium) {
      basePayload.premium = travelPolicy.premium.toString();
    }

    // Add Europe-specific fields for EUR products
    if (prodCode.startsWith('EUR_')) {
      if (travelPolicy.medical_expense_amount || travelPolicy.medicalExpenseAmount) {
        basePayload.medicalExpenseAmount = (travelPolicy.medical_expense_amount || travelPolicy.medicalExpenseAmount).toString();
      }
      if (travelPolicy.repatriation_expense_amount || travelPolicy.repatriationExpenseAmount) {
        basePayload.repatriationExpenseAmount = (travelPolicy.repatriation_expense_amount || travelPolicy.repatriationExpenseAmount).toString();
      }
    }

    // Add coverage limits if available
    if (travelPolicy.coverage_limits || travelContext?.coverageLimits) {
      basePayload.coverageLimits = travelPolicy.coverage_limits || travelContext.coverageLimits;
    }

    // Add family size indicator for large families (5+ members)
    if (insuredData.length >= 5) {
      basePayload.txtCodExt2 = '99';
    }

    return basePayload;
    /* eslint-enable sonarjs/no-duplicate-string */
  }

  static getMapfreZone(region, selectedCountry) {
    if (selectedCountry && findDestinationCountry(selectedCountry)) {
      return getMapfreDestinationZone(selectedCountry);
    }

    // Fallback to region-based zone mapping
    if (!region) return '1';
    const regionLower = region.toLowerCase();
    if (regionLower.includes('europe')) return '2';
    if (regionLower.includes('asia')) return '12';
    if (regionLower.includes('america') || regionLower.includes('worldwide')) return '3';
    return '1'; // Africa default
  }

  static getCountryCode(destination) {
    if (!destination) return null;

    // Get comprehensive country list
    const countries = TravelPolicyService.getDestinationCountries();
    const destinationLower = destination.toLowerCase();

    // Try to find exact match first
    const exactMatch = countries.find((country) => country.TRC_NAME.toLowerCase() === destinationLower);
    if (exactMatch) {
      return exactMatch.TRC_SHT_DESC;
    }

    // Try partial matching
    const partialMatch = countries.find((country) => country.TRC_NAME.toLowerCase().includes(destinationLower)
      || destinationLower.includes(country.TRC_NAME.toLowerCase()));
    if (partialMatch) {
      return partialMatch.TRC_SHT_DESC;
    }

    return null; // Return null if no match found
  }

  static getDestinationCountry(region) {
    if (!region) return null;

    // Get comprehensive country list
    const countries = TravelPolicyService.getDestinationCountries();
    const regionLower = region.toLowerCase();

    // Try to find exact match first
    const exactMatch = countries.find((country) => country.TRC_NAME.toLowerCase() === regionLower);
    if (exactMatch) {
      return exactMatch.TRC_SHT_DESC;
    }

    // Try partial matching for region names
    const partialMatch = countries.find((country) => country.TRC_NAME.toLowerCase().includes(regionLower)
      || regionLower.includes(country.TRC_NAME.toLowerCase()));
    if (partialMatch) {
      return partialMatch.TRC_SHT_DESC;
    }

    return null; // Return null if no match found
  }

  static getCountryByCode(code) {
    // Get country data from the destination countries list
    const countries = TravelPolicyService.getDestinationCountries();
    return countries.find((country) => country.TRC_SHT_DESC === code);
  }

  static getDestinationCountries() {
    // Same country list as in StartTravel.vue
    return [
      { TRC_SHT_DESC: 'DZ', TRC_NAME: 'ALGERIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'AO', TRC_NAME: 'ANGOLA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'BJ', TRC_NAME: 'BENIN', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'BW', TRC_NAME: 'BOTSWANA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'BF', TRC_NAME: 'BURKINA FASO', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'BI', TRC_NAME: 'BURUNDI', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'CM', TRC_NAME: 'CAMEROON', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'CV', TRC_NAME: 'CAPE VERDE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'CF', TRC_NAME: 'CENTRAL AFRICAN REPUBLIC', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'TD', TRC_NAME: 'CHAD', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'KM', TRC_NAME: 'COMOROS', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'CG', TRC_NAME: 'CONGO', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'CD', TRC_NAME: 'CONGO (DEMOCRATIC REPUBLIC)', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'CI', TRC_NAME: 'COTE D\'IVOIRE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'DJ', TRC_NAME: 'DJIBOUTI', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'EG', TRC_NAME: 'EGYPT', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'GQ', TRC_NAME: 'EQUATORIAL GUINEA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ER', TRC_NAME: 'ERITREA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ET', TRC_NAME: 'ETHIOPIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'GA', TRC_NAME: 'GABON', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'GM', TRC_NAME: 'GAMBIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'GH', TRC_NAME: 'GHANA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'GN', TRC_NAME: 'GUINEA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'GW', TRC_NAME: 'GUINEA-BISSAU', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'KE', TRC_NAME: 'KENYA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'LS', TRC_NAME: 'LESOTHO', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'LR', TRC_NAME: 'LIBERIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'LY', TRC_NAME: 'LIBYA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'MG', TRC_NAME: 'MADAGASCAR', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'MW', TRC_NAME: 'MALAWI', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ML', TRC_NAME: 'MALI', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'MR', TRC_NAME: 'MAURITANIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'MU', TRC_NAME: 'MAURITIUS', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'YT', TRC_NAME: 'MAYOTTE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'MA', TRC_NAME: 'MOROCCO', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'MZ', TRC_NAME: 'MOZAMBIQUE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'NA', TRC_NAME: 'NAMIBIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'NE', TRC_NAME: 'NIGER', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'NG', TRC_NAME: 'NIGERIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'RE', TRC_NAME: 'REUNION', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'RW', TRC_NAME: 'RWANDA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ST', TRC_NAME: 'SAO TOME AND PRINCIPE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SN', TRC_NAME: 'SENEGAL', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SC', TRC_NAME: 'SEYCHELLES', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SL', TRC_NAME: 'SIERRA LEONE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SO', TRC_NAME: 'SOMALIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ZA', TRC_NAME: 'SOUTH AFRICA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SS', TRC_NAME: 'SOUTH SUDAN', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SD', TRC_NAME: 'SUDAN', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'SZ', TRC_NAME: 'ESWATINI (SWAZILAND)', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'TZ', TRC_NAME: 'TANZANIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'TG', TRC_NAME: 'TOGO', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'TN', TRC_NAME: 'TUNISIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'UG', TRC_NAME: 'UGANDA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ZM', TRC_NAME: 'ZAMBIA', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'ZW', TRC_NAME: 'ZIMBABWE', TRC_TRZ_CODE: 1 },
      { TRC_SHT_DESC: 'AF', TRC_NAME: 'AFGHANISTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'AM', TRC_NAME: 'ARMENIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'AZ', TRC_NAME: 'AZERBAIJAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'BH', TRC_NAME: 'BAHRAIN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'BD', TRC_NAME: 'BANGLADESH', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'BT', TRC_NAME: 'BHUTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'BN', TRC_NAME: 'BRUNEI', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'KH', TRC_NAME: 'CAMBODIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'CN', TRC_NAME: 'CHINA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'GE', TRC_NAME: 'GEORGIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'HK', TRC_NAME: 'HONG KONG', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'IN', TRC_NAME: 'INDIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'ID', TRC_NAME: 'INDONESIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'IR', TRC_NAME: 'IRAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'IQ', TRC_NAME: 'IRAQ', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'IL', TRC_NAME: 'ISRAEL', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'JP', TRC_NAME: 'JAPAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'JO', TRC_NAME: 'JORDAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'KZ', TRC_NAME: 'KAZAKHSTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'KW', TRC_NAME: 'KUWAIT', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'KG', TRC_NAME: 'KYRGYZSTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'LA', TRC_NAME: 'LAOS', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'LB', TRC_NAME: 'LEBANON', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'MO', TRC_NAME: 'MACAU', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'MY', TRC_NAME: 'MALAYSIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'MV', TRC_NAME: 'MALDIVES', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'MN', TRC_NAME: 'MONGOLIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'MM', TRC_NAME: 'MYANMAR (BURMA)', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'NP', TRC_NAME: 'NEPAL', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'KP', TRC_NAME: 'NORTH KOREA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'OM', TRC_NAME: 'OMAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'PK', TRC_NAME: 'PAKISTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'PS', TRC_NAME: 'PALESTINE', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'PH', TRC_NAME: 'PHILIPPINES', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'QA', TRC_NAME: 'QATAR', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'SA', TRC_NAME: 'SAUDI ARABIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'SG', TRC_NAME: 'SINGAPORE', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'KR', TRC_NAME: 'SOUTH KOREA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'LK', TRC_NAME: 'SRI LANKA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'SY', TRC_NAME: 'SYRIA', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'TW', TRC_NAME: 'TAIWAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'TJ', TRC_NAME: 'TAJIKISTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'TH', TRC_NAME: 'THAILAND', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'TL', TRC_NAME: 'TIMOR-LESTE', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'TR', TRC_NAME: 'TURKEY', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'TM', TRC_NAME: 'TURKMENISTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'AE', TRC_NAME: 'UNITED ARAB EMIRATES', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'UZ', TRC_NAME: 'UZBEKISTAN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'VN', TRC_NAME: 'VIETNAM', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'YE', TRC_NAME: 'YEMEN', TRC_TRZ_CODE: 2 },
      { TRC_SHT_DESC: 'US', TRC_NAME: 'UNITED STATES', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CA', TRC_NAME: 'CANADA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'MX', TRC_NAME: 'MEXICO', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'BZ', TRC_NAME: 'BELIZE', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CR', TRC_NAME: 'COSTA RICA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'SV', TRC_NAME: 'EL SALVADOR', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'GT', TRC_NAME: 'GUATEMALA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'HN', TRC_NAME: 'HONDURAS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'NI', TRC_NAME: 'NICARAGUA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PA', TRC_NAME: 'PANAMA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'AI', TRC_NAME: 'ANGUILLA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'AG', TRC_NAME: 'ANTIGUA AND BARBUDA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'AW', TRC_NAME: 'ARUBA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'BS', TRC_NAME: 'BAHAMAS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'BB', TRC_NAME: 'BARBADOS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'BM', TRC_NAME: 'BERMUDA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'VG', TRC_NAME: 'BRITISH VIRGIN ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'KY', TRC_NAME: 'CAYMAN ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CU', TRC_NAME: 'CUBA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CW', TRC_NAME: 'CURACAO', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'DM', TRC_NAME: 'DOMINICA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'DO', TRC_NAME: 'DOMINICAN REPUBLIC', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'GD', TRC_NAME: 'GRENADA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'GP', TRC_NAME: 'GUADELOUPE', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'HT', TRC_NAME: 'HAITI', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'JM', TRC_NAME: 'JAMAICA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'MQ', TRC_NAME: 'MARTINIQUE', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'MS', TRC_NAME: 'MONTSERRAT', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PR', TRC_NAME: 'PUERTO RICO', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'KN', TRC_NAME: 'SAINT KITTS AND NEVIS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'LC', TRC_NAME: 'SAINT LUCIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'VC', TRC_NAME: 'SAINT VINCENT AND THE GRENADINES', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'TT', TRC_NAME: 'TRINIDAD AND TOBAGO', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'TC', TRC_NAME: 'TURKS AND CAICOS ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'VI', TRC_NAME: 'US VIRGIN ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'AR', TRC_NAME: 'ARGENTINA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'BO', TRC_NAME: 'BOLIVIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'BR', TRC_NAME: 'BRAZIL', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CL', TRC_NAME: 'CHILE', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CO', TRC_NAME: 'COLOMBIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'EC', TRC_NAME: 'ECUADOR', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'FK', TRC_NAME: 'FALKLAND ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'GF', TRC_NAME: 'FRENCH GUIANA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'GY', TRC_NAME: 'GUYANA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PY', TRC_NAME: 'PARAGUAY', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PE', TRC_NAME: 'PERU', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'SR', TRC_NAME: 'SURINAME', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'UY', TRC_NAME: 'URUGUAY', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'VE', TRC_NAME: 'VENEZUELA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'AU', TRC_NAME: 'AUSTRALIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'NZ', TRC_NAME: 'NEW ZEALAND', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'FJ', TRC_NAME: 'FIJI', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PG', TRC_NAME: 'PAPUA NEW GUINEA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'NC', TRC_NAME: 'NEW CALEDONIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PF', TRC_NAME: 'FRENCH POLYNESIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'SB', TRC_NAME: 'SOLOMON ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'VU', TRC_NAME: 'VANUATU', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'WS', TRC_NAME: 'SAMOA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'TO', TRC_NAME: 'TONGA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'CK', TRC_NAME: 'COOK ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'PW', TRC_NAME: 'PALAU', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'MH', TRC_NAME: 'MARSHALL ISLANDS', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'FM', TRC_NAME: 'MICRONESIA', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'KI', TRC_NAME: 'KIRIBATI', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'TV', TRC_NAME: 'TUVALU', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'NR', TRC_NAME: 'NAURU', TRC_TRZ_CODE: 3 },
      { TRC_SHT_DESC: 'AL', TRC_NAME: 'ALBANIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'AD', TRC_NAME: 'ANDORRA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'AT', TRC_NAME: 'AUSTRIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'BY', TRC_NAME: 'BELARUS', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'BE', TRC_NAME: 'BELGIUM', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'BA', TRC_NAME: 'BOSNIA AND HERZEGOVINA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'BG', TRC_NAME: 'BULGARIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'HR', TRC_NAME: 'CROATIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'CY', TRC_NAME: 'CYPRUS', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'CZ', TRC_NAME: 'CZECH REPUBLIC', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'DK', TRC_NAME: 'DENMARK', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'EE', TRC_NAME: 'ESTONIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'FI', TRC_NAME: 'FINLAND', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'FR', TRC_NAME: 'FRANCE', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'DE', TRC_NAME: 'GERMANY', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'GI', TRC_NAME: 'GIBRALTAR', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'GR', TRC_NAME: 'GREECE', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'HU', TRC_NAME: 'HUNGARY', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'IS', TRC_NAME: 'ICELAND', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'IE', TRC_NAME: 'IRELAND', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'IT', TRC_NAME: 'ITALY', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'XK', TRC_NAME: 'KOSOVO', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'LV', TRC_NAME: 'LATVIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'LI', TRC_NAME: 'LIECHTENSTEIN', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'LT', TRC_NAME: 'LITHUANIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'LU', TRC_NAME: 'LUXEMBOURG', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'MT', TRC_NAME: 'MALTA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'MD', TRC_NAME: 'MOLDOVA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'MC', TRC_NAME: 'MONACO', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'ME', TRC_NAME: 'MONTENEGRO', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'NL', TRC_NAME: 'NETHERLANDS', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'MK', TRC_NAME: 'NORTH MACEDONIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'NO', TRC_NAME: 'NORWAY', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'PL', TRC_NAME: 'POLAND', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'PT', TRC_NAME: 'PORTUGAL', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'RO', TRC_NAME: 'ROMANIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'RU', TRC_NAME: 'RUSSIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'SM', TRC_NAME: 'SAN MARINO', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'RS', TRC_NAME: 'SERBIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'SK', TRC_NAME: 'SLOVAKIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'SI', TRC_NAME: 'SLOVENIA', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'ES', TRC_NAME: 'SPAIN', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'SE', TRC_NAME: 'SWEDEN', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'CH', TRC_NAME: 'SWITZERLAND', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'UA', TRC_NAME: 'UKRAINE', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'GB', TRC_NAME: 'UNITED KINGDOM', TRC_TRZ_CODE: 4 },
      { TRC_SHT_DESC: 'VA', TRC_NAME: 'VATICAN CITY', TRC_TRZ_CODE: 4 },
    ];
  }

  static getProductCode(travelPolicy, travelContext) {
    // Get the destination zone from selected country or region
    const selectedCountry = travelContext?.selectedCountry || travelPolicy.selectedCountry;
    // console.log('getProductCode - selectedCountry:', selectedCountry, 'from context:', travelContext?.selectedCountry, 'from policy:', travelPolicy.selectedCountry);
    const zone = TravelPolicyService.getMapfreZone(travelPolicy.region, selectedCountry);
    // console.log('getProductCode - zone:', zone, 'region:', travelPolicy.region);
    const planType = travelPolicy.plan_type || 'family';
    const region = (travelPolicy.region || '').toLowerCase();

    // Map based on zone and plan type
    if (zone === '1') { // Africa
      return planType === 'individual' ? 'AFR_IND' : 'AFR_FAM';
    }
    if (zone === '12') { // Asia
      return planType === 'individual' ? 'ASI_IND' : 'ASI_FAM';
    }
    if (zone === '2') { // Europe
      // Check for Europe Plus vs Basic based on region name
      if (region.includes('plus')) {
        return planType === 'individual' ? 'EUR_IND_PLU' : 'EUR_FAM_PLU';
      }
      return planType === 'individual' ? 'EUR_BAS' : 'EUR_FAM_BAS';
    }
    if (zone === '3') { // Americas/Oceania - Worldwide
      // Check coverage level from region name
      if (region.includes('extra')) {
        return planType === 'individual' ? 'WW_IND_EXT' : 'WW_FAM_EXT';
      }
      if (region.includes('premium')) {
        return planType === 'individual' ? 'WW_IND_PRE' : 'WW_FAM_PRE';
      }
      return planType === 'individual' ? 'WW_IND_BAS' : 'WW_FAM_BAS';
    }

    // Check for special plan types
    if (planType === 'student') {
      return region.includes('premium') ? 'STU_PRE' : 'STU_BAS';
    }
    if (planType === 'corporate') {
      return 'CORP';
    }
    if (region.includes('domestic')) {
      return 'DOM';
    }
    if (region.includes('inbound')) {
      return 'INB';
    }

    // Smart mapping based on actual user selections
    // console.warn(`Product code mapping fallback used for region: ${region}, zone: ${zone}, planType: ${planType}`);
    // Use zone-based intelligent mapping as fallback
    if (zone === '1') return planType === 'individual' ? 'AFR_IND' : 'AFR_FAM';
    if (zone === '2') return planType === 'individual' ? 'ASI_IND' : 'ASI_FAM';
    if (zone === '4') return planType === 'individual' ? 'EUR_BAS' : 'EUR_FAM_BAS';
    if (zone === '3') return planType === 'individual' ? 'WW_IND_BAS' : 'WW_FAM_BAS';
    // Last resort - return most common family plan
    return 'AFR_FAM';
  }

  static async updateTravelPolicyStatus(policyId, mapfreData) {
    try {
      await axios.post(`/customer/digital-quotation/pacis-travel/quotes/${policyId}/policy`, mapfreData);
    } catch (error) {
      // console.warn('Failed to update travel policy status:', error);
    }
  }
}

// Test method to trigger policy issuance for debugging
// TravelPolicyService.testPolicyIssuance = async (quoteId, store = null) => {
//   // console.log('Testing policy issuance for quote:', quoteId);
//   const mockPaymentContext = {
//     productId: quoteId,
//     payableAmount: 2731,
//     email: 'test@email.com',
//     phoneNumber: '+254729680068',
//     transactionReference: travelPaymentReference(customerIdNo), // TR-{id}
//   };

//   // Try to get store from global Vue instance if not provided
//   let finalStore = store;
//   if (!finalStore && typeof window !== 'undefined' && window.$store) {
//     finalStore = window.$store;
//     // console.log('Using global store for testing');
//   }

//   return TravelPolicyService.issueTravelPolicy(mockPaymentContext, finalStore);
// };

// Make it available globally for console testing
// if (typeof window !== 'undefined') {
//   window.TravelPolicyService = TravelPolicyService;
//   // Also make store globally accessible if it's available
//   if (typeof document !== 'undefined') {
//     // Try to get store from Vue app
//     const appElement = document.querySelector('#app');
//     // eslint-disable-next-line no-underscore-dangle
//     const app = appElement?.__vue__;
//     if (app && app.$store) {
//       window.$store = app.$store;
//     }
//   }
// }

// Export singleton instance
export default new TravelPolicyService();
